/**
 * Core TypeScript types for Atlas Guard Admin UI
 */

// ============================================
// User & Authentication Types
// ============================================

export interface UserContext {
  user_id: string;
  user_email: string;
  user_name: string;
  org_id: string;
  team_id: string;
  project_id: string;
  org_name?: string | null;
  team_name?: string | null;
  project_name?: string | null;

  // User profile information
  user_picture?: string | null;
  given_name?: string | null;
  family_name?: string | null;
  locale?: string | null;
  email_verified?: boolean;

  global_role: string;
  team_role?: string | null;
  project_role?: string | null;
  permissions: string[];
  mfa_verified?: boolean;
  device_trusted?: boolean;
}

export enum RoleType {
  SUPER_ADMIN = "super_admin",
  ORG_ADMIN = "org_admin",
  TEAM_ADMIN = "team_admin",
  PROJECT_ADMIN = "project_admin",
  VIEWER = "viewer",
}

export enum Permission {
  // Organization permissions
  READ_ORG = "read:org",
  WRITE_ORG = "write:org",
  DELETE_ORG = "delete:org",

  // Team permissions
  READ_TEAM = "read:team",
  WRITE_TEAM = "write:team",
  DELETE_TEAM = "delete:team",
  MANAGE_TEAM_USERS = "manage:team_users",

  // Project permissions
  READ_PROJECT = "read:project",
  WRITE_PROJECT = "write:project",
  DELETE_PROJECT = "delete:project",
  MANAGE_PROJECT_USERS = "manage:project_users",

  // Service permissions
  EXECUTE_SERVICES = "execute:services",

  // API Key permissions
  READ_API_KEYS = "read:api_keys",
  WRITE_API_KEYS = "write:api_keys",
  DELETE_API_KEYS = "delete:api_keys",

  // User permissions
  READ_USERS = "read:users",
  WRITE_USERS = "write:users",
  DELETE_USERS = "delete:users",

  // Routing permissions
  READ_ROUTES = "read:routes",
  WRITE_ROUTES = "write:routes",
  DELETE_ROUTES = "delete:routes",

  // Policy permissions
  READ_POLICIES = "read:policies",
  WRITE_POLICIES = "write:policies",
  DELETE_POLICIES = "delete:policies",

  // Team member permissions
  READ_TEAM_MEMBERS = "read:team_members",
  WRITE_TEAM_MEMBERS = "write:team_members",

  // Project member permissions
  READ_PROJECT_MEMBERS = "read:project_members",
  WRITE_PROJECT_MEMBERS = "write:project_members",

  // Organization user permissions
  READ_ORG_USERS = "read:org_users",

  // All users permission (super admin)
  READ_ALL_USERS = "read:all_users",
}

// ============================================
// Team Types
// ============================================

export interface Team {
  team_id: string;
  team_name: string;
  org_id: string;
  description?: string;
  status: "active" | "inactive" | "suspended";
  created_at: string;
  updated_at: string;
  created_by: string;
  member_count?: number;
  project_count?: number;
}

export interface TeamCreate {
  team_name: string;
  org_id: string;
  description?: string;
  status?: "active" | "inactive";
}

export interface TeamUpdate {
  team_name?: string;
  description?: string;
  status?: "active" | "inactive" | "suspended";
}

export interface TeamMember {
  user_id: string;
  user_email: string;
  user_name: string;
  role: RoleType;
  permissions: Permission[];
  assigned_at: string;
  assigned_by: string;
}

// ============================================
// Project Types
// ============================================

export interface Project {
  project_id: string;
  project_name: string;
  team_id: string;
  org_id: string;
  description?: string;
  status: "active" | "inactive" | "suspended";
  service_allowlist: string[];
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
  created_by: string;
}

export interface ProjectCreate {
  project_name: string;
  team_id: string;
  org_id: string;
  description?: string;
  status?: "active" | "inactive";
  service_allowlist?: string[];
  metadata?: Record<string, any>;
}

export interface ProjectUpdate {
  project_name?: string;
  description?: string;
  status?: "active" | "inactive" | "suspended";
  service_allowlist?: string[];
  metadata?: Record<string, any>;
}

// ============================================
// User Types
// ============================================

export interface User {
  user_id: string;
  user_email: string;
  user_name: string;
  org_id: string;
  global_role: RoleType;
  status: "active" | "inactive" | "suspended";
  profile?: UserProfile;
  created_at: string;
  updated_at: string;
  last_login?: string;
}

export interface UserProfile {
  avatar_url?: string;
  phone?: string;
  timezone?: string;
  language?: string;
}

export interface UserMembership {
  user_id: string;
  team_id?: string;
  project_id?: string;
  role: RoleType;
  permissions: Permission[];
  assigned_at: string;
  assigned_by: string;
}

// ============================================
// API Key Types
// ============================================

export interface APIKey {
  key_id: string;
  key_name: string;
  project_id: string;
  team_id: string;
  org_id: string;
  key_hash: string;
  key_prefix: string;
  scopes: string[];
  status: "active" | "revoked" | "expired";
  created_at: string;
  expires_at?: string;
  last_used_at?: string;
  created_by: string;
  usage_count?: number;
}

export interface APIKeyCreate {
  key_name: string;
  project_id: string;
  scopes?: string[];
  expires_at?: string;
}

export interface APIKeyResponse {
  key_id: string;
  key: string; // Only returned once during creation
  key_name: string;
  project_id: string;
  created_at: string;
}

// ============================================
// Routing Configuration Types
// ============================================

export interface RouteConfig {
  service: string;
  path: string;
  backend_url: string;
  methods?: string[];
  auth_required?: boolean;
}

export interface ServicePolicy {
  service_account: string;
  allowed_services: string[] | "*";
  rate_limit?: number;
  quota_multiplier?: number;
  description?: string;
}

export interface TeamPolicy {
  team_id: string;
  allowed_services: string[];
  rate_limit?: number;
  quota_multiplier?: number;
  description?: string;
}

// ============================================
// Analytics Types
// ============================================

export interface UsageMetrics {
  total_requests: number;
  successful_requests: number;
  failed_requests: number;
  avg_response_time: number;
  period: string;
  timestamp: string;
}

export interface ServiceUsage {
  service_name: string;
  request_count: number;
  error_count: number;
  avg_latency: number;
}

export interface QuotaUsage {
  team_id: string;
  team_name: string;
  quota_limit: number;
  quota_used: number;
  quota_remaining: number;
  period: string;
}

// ============================================
// API Response Types
// ============================================

export interface APIResponse<T> {
  data: T;
  message?: string;
  timestamp: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
}

export interface APIError {
  error: string;
  message: string;
  code: string;
  status: number;
  details?: Record<string, any>;
}

// ============================================
// UI State Types
// ============================================

export interface UIState {
  sidebarOpen: boolean;
  theme: "light" | "dark";
  currentTeam: string | null;
  currentProject: string | null;
}

export interface AuthState {
  user: UserContext | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

// ============================================
// Form Types
// ============================================

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RoleAssignment {
  user_id: string;
  role: RoleType;
  team_id?: string;
  project_id?: string;
}

// ============================================
// Utility Types
// ============================================

export type Status = "active" | "inactive" | "suspended";
export type LoadingState = "idle" | "loading" | "success" | "error";

export interface SelectOption {
  label: string;
  value: string;
}
